/**
 * Library: public originals to arrange, found by composer, title or opus, and narrowed to the
 * scores written for one instrument.
 *
 * Every score here is a transcription released under a licence the row names, of authors who
 * died long enough ago to be public domain in the EU and the UK, and each row says so with a
 * link to the file at the version it was taken from -- or to the data set it was taken out
 * of -- and, where the collection records it, to the printed edition the transcription was
 * made from, or to the work's page on IMSLP where nobody checked it against one. A row says
 * when its score is a community transcription. Choosing one opens it on Arrange by its id,
 * so what is arranged is exactly the file the row describes.
 *
 * The list is of works: a work the library holds in more than one score -- its movements, a
 * set's songs, every upload of it -- is one row that opens on them, and the page counts works.
 *
 * The search runs on the server, over the whole catalogue, a page at a time: the catalogue is
 * a few thousand rows and a phone has no business holding all of them to filter one word.
 */

import { useEffect, useState } from "react";
import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import Board from "../components/Board";
import InstrumentPicker from "../components/InstrumentPicker";
import WorkRow from "../components/LibraryRows";
import { Failed, Loading } from "../components/Page";
import { count } from "../lib/format";

/** How long typing has to pause before the search is sent. */
const SETTLE_MS = 250;

const CHIP = "h-10 shrink-0 rounded-full border px-3.5 text-[13px] font-medium";
const EXTERNAL = "underline decoration-rule underline-offset-2 hover:text-ink";

export default function LibraryPage(): JSX.Element {
  const [typed, setTyped] = useState("");
  const [query, setQuery] = useState("");
  const [collection, setCollection] = useState<string | null>(null);
  const [instrument, setInstrument] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuery(typed.trim());
      setOffset(0);
    }, SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [typed]);

  const key = JSON.stringify([query, collection, instrument, offset]);
  const { data, error, loading } = useAsync(key, () =>
    api.library({ q: query, collection, instrument, offset }),
  );
  // The chips and the instruments are drawn from the last answer that had them, so they do
  // not blink out while the next page is on its way.
  const [collections, setCollections] = useState(data?.collections ?? []);
  const [instruments, setInstruments] = useState(data?.instruments ?? []);
  const [labels, setLabels] = useState<Record<string, string>>({});
  useEffect(() => {
    if (data === null) return;
    setCollections(data.collections);
    setInstruments(data.instruments);
    setLabels((known) => ({
      ...known,
      ...Object.fromEntries(data.instruments.map((one) => [one.key, one.label])),
    }));
  }, [data]);
  // An instrument picked in one collection stays picked in another that has none of it, and
  // is offered there with its count of nothing rather than silently dropped.
  const offered =
    instrument === null || instruments.some((one) => one.key === instrument)
      ? instruments
      : [...instruments, { key: instrument, label: labels[instrument] ?? instrument, scores: 0 }];

  const all = collections.reduce((sum, one) => sum + one.scores, 0);
  const pick = (next: string | null): void => {
    setCollection(next);
    setOffset(0);
  };

  return (
    <Board
      kicker="Public originals"
      title="Library"
      lede="Scores in the public domain, each transcribed and released under the licence its row names. Choose one to arrange it for the players you pick. A work kept in more than one score is one row: open it for its movements and every transcription of it."
      measure="wide"
    >
      <div className="space-y-5">
        <div className="sticky top-0 z-10 -mx-1 flex flex-wrap items-center gap-2 bg-paper px-1 py-2">
          <input
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            placeholder="Composer, title or opus"
            aria-label="Search the library"
            type="search"
            className="h-10 min-w-[12rem] flex-1 rounded-[10px] border border-rule bg-leaf px-3.5 text-sm outline-none focus:border-ink"
          />
          <InstrumentPicker
            look="paper"
            label="Written for"
            placeholder="Any instrument"
            value={instrument ?? ""}
            options={[
              { value: "", label: "Any instrument" },
              ...offered.map((one) => ({
                value: one.key,
                label: `${one.label} · ${count(one.scores)}`,
              })),
            ]}
            onPick={(picked) => {
              setInstrument(picked === "" ? null : picked);
              setOffset(0);
            }}
            className="w-56 shrink-0"
          />
          {[{ key: null, name: "All", scores: all }, ...collections].map((one) => (
            <button
              key={one.key ?? "all"}
              type="button"
              onClick={() => pick(one.key)}
              aria-pressed={collection === one.key}
              className={`${CHIP} ${
                collection === one.key
                  ? "border-ink bg-ink text-paper"
                  : "border-rule bg-leaf text-ink-faded"
              }`}
            >
              {one.name.replace(/^OpenScore /, "")} {count(one.scores)}
            </button>
          ))}
        </div>

        {loading && data === null ? <Loading what="the library" /> : null}
        {error === null ? null : <Failed error={error} />}
        {data === null ? null : (
          <>
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-xs text-ink-quiet">
              <span>
                {data.total === 0
                  ? "No works"
                  : `${count(data.offset + 1)}–${count(data.offset + data.works.length)} of ${count(data.total)} ${data.total === 1 ? "work" : "works"}`}
              </span>
              {data.total === 0 ? null : (
                <span>
                  {count(data.scores)} {data.scores === 1 ? "score" : "scores"} in{" "}
                  {data.total === 1 ? "it" : "them"}
                </span>
              )}
            </div>

            <ul className="overflow-hidden rounded-xl border border-rule bg-leaf">
              {data.works.length === 0 ? (
                <li className="px-4 py-6 text-sm text-ink-faded">Nothing matches that.</li>
              ) : (
                data.works.map((work) => (
                  <WorkRow
                    key={work.scores[0]?.id ?? work.name}
                    work={work}
                    opened={data.total === 1}
                  />
                ))
              )}
            </ul>

            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                disabled={data.offset === 0}
                onClick={() => setOffset(Math.max(0, data.offset - data.limit))}
                className={`${CHIP} border-rule bg-leaf disabled:opacity-40`}
              >
                Previous
              </button>
              <button
                type="button"
                disabled={data.offset + data.works.length >= data.total}
                onClick={() => setOffset(data.offset + data.limit)}
                className={`${CHIP} border-rule bg-leaf disabled:opacity-40`}
              >
                Next
              </button>
            </div>

            <footer className="space-y-1 border-t border-rule pt-4 text-[11px] leading-relaxed text-ink-quiet">
              {data.collections.map((one) => (
                <p key={one.key}>
                  {one.attribution}.{" "}
                  <a className={EXTERNAL} href={one.url} target="_blank" rel="noreferrer">
                    {one.pinned}
                  </a>
                </p>
              ))}
              <p>Catalogue built {data.builtAt.slice(0, 10)}.</p>
            </footer>
          </>
        )}
      </div>
    </Board>
  );
}
